import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import type { CartPanelFlightItem } from '../hooks/useCartPanelItems'
import {
  cartPanelMobileTileBodyStyles,
  cartPanelMobileTileFooterStyles,
  cartPanelMobileTileHeaderStyles,
  cartPanelMobileTileHoverStyles,
  cartPanelMobileTileRootStyles,
} from './cartPanelLayout.styles'

const MobileTileRoot = styled.div`
  ${cartPanelMobileTileRootStyles}
  ${cartPanelMobileTileHoverStyles}
`

const TileHeader = styled.div`
  ${cartPanelMobileTileHeaderStyles}
`

const TileBody = styled.div`
  ${cartPanelMobileTileBodyStyles}
`

const TileFooter = styled.div`
  ${cartPanelMobileTileFooterStyles}
`

const PriceArea = styled.div`
  flex: 1;
  min-width: 0;
`

const ActionsArea = styled.div`
  flex-shrink: 0;
`

function stopPropagation(e: MouseEvent) {
  e.stopPropagation()
}

interface Props {
  item: CartPanelFlightItem
  onToggleInclude: (item: CartPanelFlightItem) => void
  onCardClick?: () => void
  layoutTitle: string
  layoutSubtitle?: string
  headerPrefix?: ReactElement
  mobileMetaData?: ReactElement
  topBanner?: ReactNode
  extension: ReactNode | undefined
  attention: ReactNode | undefined
  footerActions: ReactElement
  price: ReactNode | undefined
  isToggleDisabled?: boolean
  isAwaitingFareSelection?: boolean
}

function CartPanelFlightMobileTileLayout({
  item,
  onToggleInclude,
  onCardClick,
  layoutTitle,
  layoutSubtitle,
  headerPrefix,
  mobileMetaData,
  topBanner,
  extension,
  attention,
  footerActions,
  price,
  isToggleDisabled = false,
  isAwaitingFareSelection = false,
}: Props) {
  const handleCheckboxChange = useCallback(() => {
    onToggleInclude(item)
  }, [onToggleInclude, item])

  const handleTileClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!onCardClick || !(event.target instanceof HTMLElement)) return
    if (didClickInteractableElement(event.target, event.currentTarget)) return
    onCardClick()
  }, [onCardClick])

  const isInteractive = !item.isBooked

  const includeCheckbox = useMemo(() => isInteractive ? (
    <div onClick={stopPropagation} data-testid="cart-panel-flight-mobile-checkbox">
      <CheckboxButton
        checked={!item.cartExcluded}
        onChange={handleCheckboxChange}
        disabled={isToggleDisabled}
        size="medium"
      />
    </div>
  ) : undefined, [handleCheckboxChange, isInteractive, isToggleDisabled, item.cartExcluded])

  return (
    <MobileTileRoot
      className={clsx({
        clickable: !!onCardClick,
        deselected: item.cartExcluded,
        'awaiting-fare': isAwaitingFareSelection,
      })}
      onClickCapture={onCardClick ? handleTileClickCapture : undefined}
      data-testid="cart-panel-flight-mobile-tile"
    >
      {topBanner && <div onClick={stopPropagation}>{topBanner}</div>}
      <TileHeader className={clsx({ 'has-checkbox': !!includeCheckbox })}>
        {includeCheckbox}
        <Group direction="vertical" gap={4}>
          {headerPrefix}
          <Heading variant="heading6">{layoutTitle}</Heading>
          {layoutSubtitle && (
            <BodyText variant="medium" colour="light-foreground-alt">{layoutSubtitle}</BodyText>
          )}
          {mobileMetaData}
        </Group>
      </TileHeader>
      <TileBody data-testid="cart-panel-flight-mobile-body">
        {attention && <div onClick={stopPropagation}>{attention}</div>}
        {extension && (
          <div onClick={stopPropagation} data-testid="cart-panel-flight-mobile-extension">
            {extension}
          </div>
        )}
        <TileFooter data-testid="cart-panel-flight-mobile-footer">
          <PriceArea>{price}</PriceArea>
          <ActionsArea onClick={stopPropagation}>{footerActions}</ActionsArea>
        </TileFooter>
      </TileBody>
    </MobileTileRoot>
  )
}

export default CartPanelFlightMobileTileLayout
